import React from "react";
import { ClipboardList, Mic2, HardHat } from "lucide-react";

export const professionOptions = [
  {
    id: "planner",
    label: "Planner",
    icon: ClipboardList,
    description: "Plan and manage events from concept to execution.",
  },
  {
    id: "performer",
    label: "Performer",
    icon: Mic2,
    description: "Provide artistic or entertainment services for events.",
  },
  {
    id: "crew",
    label: "Crew",
    icon: HardHat,
    description: "Support events with technical or operational expertise.",
  },
];

export default function ProfessionalRegistrationLayout({
  title,
  description,
  currentRole,
  onRoleChange,
  isRoleFrozen = false,
  roleError,
  // Footer button props
  onBack,
  onContinue,
  continueLabel = "Continue",
  isLoading = false,
  isDisabled = false,
  children,
}) {
  return (
    <div
      className="w-full font-sans flex flex-col"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      {/* Header Section */}
      <div className="flex flex-col pb-1 gap-0.5 text-center shrink-0 pt-0 -mt-2">
        <h1 className="text-xl md:text-2xl font-extrabold text-gray-900">
          {title}
        </h1>
        <p className="text-gray-500 text-sm max-w-2xl mx-auto">
          {description}
        </p>
      </div>

      {/* Main 2-col layout */}
      <div className="flex flex-col lg:flex-row gap-3 flex-1 min-h-[400px] md:min-h-[450px] lg:min-h-[500px]">
        {/* Left Panel: Profession Sidebar */}
        <div className="w-full lg:w-[28%] rounded-2xl p-3 md:p-4 border border-gray-100 shadow-sm flex flex-col bg-white">
          <div className="mb-1">
            <h2
              className="text-base md:text-lg text-gray-900"
              style={{ fontFamily: "CabinetGrotesk-Bold" }}
            >
              Profession Type
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Select the category that best describes your services.
            </p>
          </div>
          <hr className="border-gray-200 w-full my-3" />

          {roleError && (
            <div className="w-full mb-3 border border-[#FF4C18] bg-[#FFF0EA] rounded-xl p-3 text-sm">
              <span className="text-[#FF4C18] font-medium">{roleError}</span>
            </div>
          )}

          <div className="flex flex-col gap-2.5">
            {professionOptions.map((opt) => {
              const Icon = opt.icon;
              const isActive = currentRole === opt.id;

              const cardClasses = `flex flex-col p-3 rounded-xl border text-left transition-all duration-200 ${
                isActive
                  ? "bg-[#FFF0EA] border-[#FF4C18] text-[#FF4C18] shadow-md"
                  : isRoleFrozen
                  ? "bg-white border-transparent opacity-40"
                  : "bg-white border-gray-100 hover:border-gray-200 hover:shadow-sm"
              }`;

              const content = (
                <>
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <Icon
                      className={`w-5 h-5 ${isActive ? "text-[#FF4C18]" : "text-gray-500"}`}
                    />
                    <span
                      className={`text-base ${isActive ? "text-[#FF4C18]" : "text-gray-600"}`}
                      style={{ fontFamily: "CabinetGrotesk-Bold" }}
                    >
                      {opt.label}
                    </span>
                  </div>
                  <p
                    className={`text-xs leading-relaxed ${isActive ? "text-[#FF4C18] opacity-80" : "text-gray-500"}`}
                    style={{ fontFamily: "CabinetGrotesk-Regular" }}
                  >
                    {opt.description}
                  </p>
                </>
              );

              if (isRoleFrozen) {
                return (
                  <div key={opt.id} className={cardClasses}>
                    {content}
                  </div>
                );
              }

              return (
                <button
                  key={opt.id}
                  type="button"
                  disabled={isLoading}
                  onClick={() => onRoleChange && onRoleChange(opt.id)}
                  className={`${cardClasses} ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {content}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Panel: Form Fields */}
        <div 
          className="w-full lg:w-[72%] bg-white border border-gray-100 rounded-2xl p-4 md:p-5 shadow-sm flex flex-col overflow-y-auto no-scrollbar"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <div className="flex-1">
            {children}
          </div>
        </div>
      </div>

      {/* Common Footer Buttons */}
      <div className={`w-full flex ${onBack ? "justify-between" : "justify-end"} items-center mt-3 shrink-0`}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className={`min-w-[100px] px-6 py-2.5 border rounded-xl font-bold transition-colors text-sm ${
              isLoading
                ? "border-gray-300 text-gray-400 cursor-not-allowed"
                : "border-[#FF4C18] text-[#FF4C18] hover:bg-orange-50"
            }`}
          >
            Back
          </button>
        )}
        <button
          type="button"
          disabled={isLoading}
          onClick={onContinue}
          className={`min-w-[120px] px-6 py-2.5 bg-[#FF4C18] text-white rounded-xl font-bold transition-all shadow-md shadow-orange-100 active:scale-95 text-sm ${
            isDisabled || isLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-orange-700"
          }`}
        >
          {isLoading ? (continueLabel !== "Continue" ? continueLabel : "Please wait...") : continueLabel}
        </button>
      </div>
    </div>
  );
}
